import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { useModuleStatus } from '@/hooks/useModuleStatus.ts'
import {
  CalendarRange, ChevronLeft, ChevronRight,
  MapPin, Clock, AlertTriangle,
} from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  BetaBadge,
  Button,
  Card,
  EmptyState,
  IconButton,
  PageHeader,
  SimpleTable,
} from '@/components/ui'

interface TechnicianSchedule {
  id: string
  name: string
  color: string
  appointments: ScheduleEntry[]
}

interface ScheduleEntry {
  id: string
  title: string
  customerName?: string
  start: string
  end: string
  type: 'appointment' | 'ticket' | 'internal'
  location?: string
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr']

function getWeekDays(date: Date): Date[] {
  const d = new Date(date)
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  const monday = new Date(d.setDate(diff))
  return Array.from({ length: 5 }, (_, i) =>
    new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i),
  )
}

function getWeekNumber(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
}

const TECH_COLORS = [
  'bg-blue-100 border-blue-300',
  'bg-green-100 border-green-300',
  'bg-purple-100 border-purple-300',
  'bg-amber-100 border-amber-300',
  'bg-pink-100 border-pink-300',
]

export function TerminplanerPage() {
  const a = useAnonymize()
  const { isBeta } = useModuleStatus('terminplaner')
  const [currentDate, setCurrentDate] = useState(new Date())

  const weekDays = getWeekDays(currentDate)
  const weekNum = getWeekNumber(currentDate)

  const { data: schedules = [] } = useQuery<TechnicianSchedule[]>({
    queryKey: ['team-schedule', currentDate.toISOString()],
    queryFn: () => apiClient.get('/schedule/team', { params: { week: currentDate.toISOString() } }).then((r) => r.data),
    retry: false,
  })

  const navigate = (dir: number) => {
    const d = new Date(currentDate)
    d.setDate(d.getDate() + dir * 7)
    setCurrentDate(d)
  }

  return (
    <div className="space-y-loose">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-snug">
            <CalendarRange className="w-7 h-7 text-brand-500" />
            Terminplaner
            {isBeta && <BetaBadge size="md" />}
          </span>
        }
        description="Team-Kalender und Einsatzplanung"
      />

      {/* Wochennavigation */}
      <Card>
        <div className="flex items-center justify-between gap-3 px-4 py-2 flex-wrap">
          <div className="flex items-center gap-snug">
            <IconButton
              icon={<ChevronLeft />}
              aria-label="Vorherige Woche"
              size="sm"
              onClick={() => navigate(-1)}
            />
            <Button variant="ghost" size="sm" onClick={() => setCurrentDate(new Date())}>
              Heute
            </Button>
            <IconButton
              icon={<ChevronRight />}
              aria-label="Nächste Woche"
              size="sm"
              onClick={() => navigate(1)}
            />
            <h2 className="text-h4 text-gray-900 ml-2">
              KW {weekNum} — {weekDays[0]!.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} bis {weekDays[4]!.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}
            </h2>
          </div>

          <div className="flex items-center gap-comfortable text-caption flex-wrap">
            {schedules.map((tech, i) => (
              <span key={tech.id} className="inline-flex items-center gap-1.5">
                <span className={cn('w-3 h-3 rounded-full border', TECH_COLORS[i % TECH_COLORS.length])} />
                {tech.name}
              </span>
            ))}
          </div>
        </div>
      </Card>

      {/* Kapazitaetsuebersicht */}
      <div className="grid grid-cols-5 gap-snug">
        {weekDays.map((day, di) => {
          const dayStr = day.toDateString()
          const isToday = dayStr === new Date().toDateString()
          const totalAppointments = schedules.reduce(
            (sum, tech) => sum + tech.appointments.filter((a) => new Date(a.start).toDateString() === dayStr).length,
            0,
          )
          return (
            <Card key={di} className={cn('p-3 text-center', isToday && 'border-brand-400 bg-brand-50')}>
              <p className="text-caption text-gray-500">{WEEKDAYS[di]}</p>
              <p className={cn('text-small font-semibold', isToday ? 'text-brand-600' : 'text-gray-900')}>
                {day.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
              </p>
              <p className="text-body-lg font-bold text-gray-900 mt-1">{totalAppointments}</p>
              <p className="text-caption text-gray-500">Termine</p>
            </Card>
          )
        })}
      </div>

      {/* Team-Plan */}
      <Card>
        {schedules.length === 0 ? (
          <EmptyState
            icon={<CalendarRange />}
            title="Keine Einsatzplanung für diese Woche"
            description="Es liegen keine Techniker- oder Termindaten für den ausgewählten Zeitraum vor."
          />
        ) : (
          <div className="overflow-x-auto">
            <SimpleTable>
              <SimpleTable.Head>
                <SimpleTable.Tr>
                  <SimpleTable.Th className="sticky left-0 bg-gray-50 w-36">Techniker</SimpleTable.Th>
                  {weekDays.map((day, di) => {
                    const isToday = day.toDateString() === new Date().toDateString()
                    return (
                      <SimpleTable.Th
                        key={di}
                        className={cn('text-center min-w-[200px]', isToday && 'text-brand-600 bg-brand-50')}
                      >
                        {WEEKDAYS[di]}, {day.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                      </SimpleTable.Th>
                    )
                  })}
                </SimpleTable.Tr>
              </SimpleTable.Head>
              <SimpleTable.Body>
                {schedules.map((tech, ti) => (
                  <SimpleTable.Tr key={tech.id}>
                    <SimpleTable.Td className="sticky left-0 bg-white">
                      <div className="flex items-center gap-snug">
                        <div className={cn('w-2.5 h-2.5 rounded-full', TECH_COLORS[ti % TECH_COLORS.length]?.split(' ')[0])} />
                        <span className="text-small font-medium text-gray-900">{tech.name}</span>
                      </div>
                    </SimpleTable.Td>
                    {weekDays.map((day, di) => {
                      const dayStr = day.toDateString()
                      const dayAppointments = tech.appointments.filter((a) => new Date(a.start).toDateString() === dayStr)
                      const isToday = dayStr === new Date().toDateString()
                      return (
                        <SimpleTable.Td
                          key={di}
                          className={cn('align-top min-w-[200px]', isToday && 'bg-brand-50/30')}
                        >
                          <div className="space-y-1">
                            {dayAppointments.map((apt) => (
                              <div
                                key={apt.id}
                                className={cn(
                                  'text-caption px-2 py-1.5 rounded border cursor-pointer hover:shadow-card transition',
                                  TECH_COLORS[ti % TECH_COLORS.length],
                                )}
                              >
                                <p className="font-medium truncate">{apt.title}</p>
                                <p className="text-gray-600 flex items-center gap-0.5">
                                  <Clock className="w-3 h-3" />
                                  {new Date(apt.start).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} – {new Date(apt.end).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                                {apt.customerName && <p className="text-gray-500 truncate">{a.name(apt.customerName)}</p>}
                                {apt.location && (
                                  <p className="text-gray-500 flex items-center gap-0.5 truncate">
                                    <MapPin className="w-3 h-3 flex-shrink-0" />
                                    {apt.location}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </SimpleTable.Td>
                      )
                    })}
                  </SimpleTable.Tr>
                ))}
              </SimpleTable.Body>
            </SimpleTable>
          </div>
        )}
      </Card>

      {/* Konflikte */}
      <Card className="border-amber-200 bg-amber-50">
        <Card.Body className="space-y-tight">
          <h3 className="text-small font-semibold text-amber-800 flex items-center gap-snug">
            <AlertTriangle className="w-4 h-4" />
            Konflikte (0)
          </h3>
          <p className="text-caption text-amber-700">Keine Terminkonflikte in dieser Woche.</p>
        </Card.Body>
      </Card>
    </div>
  )
}
